import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api from '../../services/api';
import {
  Activity, RefreshCw, Bookmark, Archive, Circle, Loader2, Filter,
  AlertTriangle, MessageCircle, Clock, FileText,
} from 'lucide-react';

// Sprint 3 — Activity Feed (nested under Communication). Themed to match the
// existing dark Communication tab (bg #0d1210 / border emerald-900/30 / text
// slate-*) while keeping the Asana-style layout: date group headers, feed
// rows with actor + verb + order-link + relative ts, +N thread badge,
// hover-revealed actions, and 4 tabs.

const TABS = [
  { id: 'activity', label: 'Activity' },
  { id: 'mentioned', label: '@Mentioned' },
  { id: 'bookmarks', label: 'Bookmarks' },
  { id: 'archive', label: 'Archive' },
];

const EVENT_TYPES = [
  { id: '', label: 'All events' },
  { id: 'documents', label: 'Documents' },
  { id: 'payments', label: 'Payments' },
  { id: 'status', label: 'Status' },
  { id: 'comments', label: 'Comments' },
];

function _groupByDate(rows) {
  const toIstStart = (dt) => {
    const asIst = new Date(dt.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    asIst.setHours(0, 0, 0, 0);
    return asIst;
  };
  const nowIst = toIstStart(new Date());
  const groups = { Today: [], Yesterday: [], 'Past 7 days': [], Older: [] };
  for (const r of rows) {
    if (!r.created_at) { groups.Older.push(r); continue; }
    const dt = new Date(r.created_at);
    const dtIst = toIstStart(dt);
    const diffDays = Math.round((nowIst - dtIst) / (24 * 3600 * 1000));
    if (diffDays <= 0) groups.Today.push(r);
    else if (diffDays === 1) groups.Yesterday.push(r);
    else if (diffDays <= 7) groups['Past 7 days'].push(r);
    else groups.Older.push(r);
  }
  return groups;
}

function _initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]).join('').toUpperCase();
}

// Deterministic per-name avatar colour. Muted tones to match the dark theme.
const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#14b8a6', '#f59e0b', '#ec4899', '#8b5cf6', '#10b981'];
function _avatarBg(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

const ActivityRow = ({ row, expanded, onToggle, onNavigateOrder, onStateChange }) => {
  const isSystem = row?.actor?.type === 'system';
  return (
    <div
      className="relative border-b border-emerald-900/20 last:border-b-0 hover:bg-[#12181a] transition-colors group"
      data-testid={`activity-row-${row.id}`}
    >
      {!row.read && (
        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-emerald-500" aria-hidden="true" />
      )}

      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer"
        onClick={() => onToggle(row.id)}
      >
        <div
          className="flex-none w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
          style={{ background: isSystem ? '#4b5563' : _avatarBg(row?.actor?.name) }}
        >
          {isSystem ? '⚙' : _initials(row?.actor?.name)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-slate-100 leading-snug">
            <b className="font-semibold text-white">{row?.actor?.name || 'System'}</b>{' '}
            <span className="text-slate-300">{row.description}</span>
            {row.order_ref_label && (
              <>
                <span className="text-slate-500">{' on '}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigateOrder?.(row.order_id); }}
                  className="text-emerald-400 hover:text-emerald-300 hover:underline font-medium"
                  data-testid={`activity-order-link-${row.id}`}
                >
                  {row.order_ref_label}
                </button>
              </>
            )}
          </div>
          {row.detail && (
            <div className="text-[12.5px] text-slate-500 mt-0.5 truncate italic">
              {`"${row.detail}"`}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-none">
          {row.thread_count > 1 && (
            <span
              className="text-[10.5px] text-slate-400 bg-[#0f1611] border border-emerald-900/40 rounded-full px-2 py-[1px]"
              data-testid={`activity-thread-badge-${row.id}`}
            >
              +{row.thread_count - 1} activities
            </span>
          )}
          {row.bookmarked && (
            <Bookmark className="w-3.5 h-3.5 text-amber-400" style={{ fill: '#f59e0b' }} />
          )}
          <span className="text-[11.5px] text-slate-500 w-8 text-right">{row.relative_time}</span>
        </div>
      </div>

      {/* Hover-revealed row actions */}
      <div
        className="absolute right-3 -top-2 hidden group-hover:flex gap-1 bg-[#0d1210] border border-emerald-900/40 rounded-lg shadow-lg p-1"
        style={{ zIndex: 3 }}
      >
        <button
          className="w-7 h-7 rounded hover:bg-[#12181a] text-slate-400 hover:text-emerald-300 flex items-center justify-center"
          title={row.read ? 'Mark unread' : 'Mark read'}
          data-testid={`activity-toggle-read-${row.id}`}
          onClick={(e) => { e.stopPropagation(); onStateChange(row.id, { read: !row.read }); }}
        >
          <Circle className="w-3 h-3" />
        </button>
        <button
          className="w-7 h-7 rounded hover:bg-[#12181a] text-slate-400 hover:text-amber-400 flex items-center justify-center"
          title={row.bookmarked ? 'Remove bookmark' : 'Bookmark'}
          data-testid={`activity-bookmark-${row.id}`}
          onClick={(e) => { e.stopPropagation(); onStateChange(row.id, { bookmarked: !row.bookmarked }); }}
        >
          <Bookmark className="w-3 h-3" />
        </button>
        <button
          className="w-7 h-7 rounded hover:bg-[#12181a] text-slate-400 hover:text-red-400 flex items-center justify-center"
          title="Archive"
          data-testid={`activity-archive-${row.id}`}
          onClick={(e) => { e.stopPropagation(); onStateChange(row.id, { archived: true }); }}
        >
          <Archive className="w-3 h-3" />
        </button>
      </div>

      {expanded && row.thread_count > 1 && (
        <div className="bg-[#0a0f0d] border-t border-dashed border-emerald-900/30 px-4 py-2 pl-16" data-testid={`activity-thread-${row.id}`}>
          {row.thread.map((t) => (
            <div key={t.id} className="flex items-start gap-2 py-1.5 border-b border-dashed border-emerald-900/20 last:border-b-0 text-[12.5px]">
              <div
                className="flex-none w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold"
                style={{ background: t?.actor?.type === 'system' ? '#4b5563' : _avatarBg(t?.actor?.name) }}
              >
                {t?.actor?.type === 'system' ? '⚙' : _initials(t?.actor?.name)}
              </div>
              <div className="flex-1 text-slate-300">
                <b className="text-white">{t?.actor?.name || 'System'}</b> {t.description}
                {t.detail && <span className="text-slate-500">{` — "${t.detail}"`}</span>}
              </div>
              <span className="text-[11px] text-slate-500">{t.relative_time}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default function ActivityFeedTab({ onNavigateOrder }) {
  const [tab, setTab] = useState('activity');
  const [eventType, setEventType] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [adminId, setAdminId] = useState('');
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ admins: [], companies: [], event_types: [] });
  // Alert banners at the top of the feed: needs-reply threads, pending doc
  // reviews, overdue orders (per stage). Refreshed on mount + after any state
  // mutation that might change the numbers.
  const [alerts, setAlerts] = useState({ needs_reply: 0, pending_reviews: 0, overdue_total: 0, overdue: {} });
  const scrollerRef = useRef(null);

  const load = useCallback(async (reset = true) => {
    setLoading(true);
    try {
      const params = { tab, limit: 25 };
      if (eventType) params.event_type = eventType;
      if (companyId) params.company_id = companyId;
      if (adminId) params.admin_id = adminId;
      if (!reset && nextCursor) params.before = nextCursor;
      const res = await api.get('/activities', { params });
      const newRows = res.data?.data || [];
      setRows(prev => reset ? newRows : [...prev, ...newRows]);
      setNextCursor(res.data?.next_cursor || null);
      setHasMore(!!res.data?.next_cursor);
    } catch (e) {
      console.error('activity feed load failed', e);
    } finally {
      setLoading(false);
    }
  }, [tab, eventType, companyId, adminId, nextCursor]);

  useEffect(() => {
    load(true);
    setExpanded(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, eventType, companyId, adminId]);

  useEffect(() => {
    api.get('/activities/filter-options')
      .then(r => setFilterOptions(r.data || {}))
      .catch(() => { /* silent */ });
  }, []);

  // Fetch alert counts on mount + refresh with the feed. Cheap query.
  const loadAlerts = useCallback(() => {
    api.get('/activities/alerts')
      .then(r => setAlerts(r.data || {}))
      .catch(() => { /* silent — decorative */ });
  }, []);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const onToggle = (id) => {
    let becomingExpanded = false;
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else { next.add(id); becomingExpanded = true; }
      return next;
    });
    if (becomingExpanded) {
      setRows(prev => prev.map(r => r.id === id ? { ...r, read: true } : r));
      api.post(`/activities/${id}/state`, { read: true }).catch(() => { /* silent */ });
    }
  };

  const onStateChange = async (id, patch) => {
    setRows(prev => {
      if (patch.archived) return prev.filter(r => r.id !== id);
      return prev.map(r => r.id === id ? { ...r, ...patch } : r);
    });
    try {
      await api.post(`/activities/${id}/state`, patch);
    } catch (e) {
      load(true);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/activities/mark-all-read');
      setRows(prev => prev.map(r => ({ ...r, read: true })));
    } catch (e) { /* silent */ }
  };

  const grouped = useMemo(() => _groupByDate(rows), [rows]);
  const orderedGroupKeys = ['Today', 'Yesterday', 'Past 7 days', 'Older'];

  const onScroll = (e) => {
    if (!hasMore || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) load(false);
  };

  return (
    <div
      ref={scrollerRef}
      onScroll={onScroll}
      className="max-h-[calc(100vh-220px)] overflow-y-auto"
      data-testid="activity-feed-tab"
    >
      {/* Alert banners — spec: 'needs attend' alerts surface on top of the feed. */}
      {(alerts.needs_reply > 0 || alerts.pending_reviews > 0 || alerts.overdue_total > 0) && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="activity-feed-alerts">
          {alerts.needs_reply > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-200 text-[12.5px]"
              data-testid="activity-alert-needs-reply"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              <span><b className="font-semibold text-red-300">{alerts.needs_reply}</b> thread{alerts.needs_reply === 1 ? '' : 's'} need{alerts.needs_reply === 1 ? 's' : ''} a reply</span>
            </div>
          )}
          {alerts.pending_reviews > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-[12.5px]"
              data-testid="activity-alert-pending-reviews"
            >
              <FileText className="w-3.5 h-3.5" />
              <span><b className="font-semibold text-amber-300">{alerts.pending_reviews}</b> document{alerts.pending_reviews === 1 ? '' : 's'} awaiting review</span>
            </div>
          )}
          {alerts.overdue_total > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-[12.5px]"
              data-testid="activity-alert-overdue"
              title={Object.entries(alerts.overdue || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`).join(' · ')}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>
                <b className="font-semibold text-amber-300">{alerts.overdue_total}</b> order{alerts.overdue_total === 1 ? '' : 's'} overdue
                {Object.entries(alerts.overdue || {}).filter(([, n]) => n > 0).length > 0 && (
                  <span className="ml-1 text-slate-400">
                    ({Object.entries(alerts.overdue || {})
                      .filter(([, n]) => n > 0)
                      .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`)
                      .join(', ')})
                  </span>
                )}
              </span>
            </div>
          )}
          <AlertTriangle className="w-3 h-3 opacity-0" />
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-[12.5px]">
        <Filter className="w-3.5 h-3.5 text-slate-500" />
        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          data-testid="activity-filter-event-type"
          className="bg-[#0f1611] border border-emerald-900/40 rounded px-2 py-1 text-slate-200 focus:outline-none focus:border-emerald-500"
        >
          {EVENT_TYPES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          data-testid="activity-filter-company"
          className="bg-[#0f1611] border border-emerald-900/40 rounded px-2 py-1 text-slate-200 max-w-[200px] focus:outline-none focus:border-emerald-500"
        >
          <option value="">All companies</option>
          {(filterOptions.companies || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={adminId}
          onChange={(e) => setAdminId(e.target.value)}
          data-testid="activity-filter-admin"
          className="bg-[#0f1611] border border-emerald-900/40 rounded px-2 py-1 text-slate-200 focus:outline-none focus:border-emerald-500"
        >
          <option value="">All admins</option>
          {(filterOptions.admins || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div className="ml-auto flex gap-2">
          <button
            onClick={markAllRead}
            data-testid="activity-mark-all-read"
            className="border border-emerald-900/40 rounded px-2.5 py-1 bg-[#0f1611] text-slate-400 hover:text-emerald-300 hover:border-emerald-500/40"
          >
            Mark all read
          </button>
          <button
            onClick={() => load(true)}
            data-testid="activity-refresh"
            className="border border-emerald-900/40 rounded px-2.5 py-1 bg-[#0f1611] text-slate-400 hover:text-emerald-300 hover:border-emerald-500/40 flex items-center gap-1.5"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Empty state */}
      {rows.length === 0 && !loading && (
        <div className="text-center text-[13px] text-slate-500 py-16 bg-[#0d1210] border border-emerald-900/20 rounded-xl" data-testid="activity-empty-state">
          <Activity className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          No activity yet.
          {tab !== 'activity' && ' Switch back to the Activity tab to see everything.'}
        </div>
      )}

      {/* Date-grouped rows */}
      {orderedGroupKeys.map(key => {
        const g = grouped[key];
        if (!g || g.length === 0) return null;
        return (
          <div key={key} className="mb-4">
            <div className="text-[11px] font-semibold text-slate-500 px-1 mb-1.5 uppercase tracking-wider">
              {key}
            </div>
            <div className="bg-[#0d1210] border border-emerald-900/30 rounded-xl overflow-hidden shadow-sm">
              {g.map(row => (
                <ActivityRow
                  key={row.id}
                  row={row}
                  expanded={expanded.has(row.id)}
                  onToggle={onToggle}
                  onNavigateOrder={onNavigateOrder}
                  onStateChange={onStateChange}
                />
              ))}
            </div>
          </div>
        );
      })}

      {loading && rows.length > 0 && (
        <div className="text-center py-4 text-slate-500 text-[13px]" data-testid="activity-loading-more">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" />
          Loading older…
        </div>
      )}
      {!hasMore && rows.length > 0 && (
        <div className="text-center py-4 text-[11.5px] text-slate-600">— that&apos;s everything within the last 180 days —</div>
      )}
    </div>
  );
}
